/** Student UI configs: statuses, filters, tabs. */
import {
  CheckCircle2,
  XCircle,
  MinusCircle,
  Clock,
  Archive,
  CalendarDays,
  UserCircle,
  FileText,
  Users,
  User,
  UserCheck,
  History,
  BookOpen,
  Home,
  Trophy,
  Heart,
  PauseCircle,
  GraduationCap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  type StudentAccountStatus,
  type StudentAccompanimentType,
  type StudentFilters,
} from '@/types/inperson/student.types';
import type { ManagedByValue } from '@/components/common/ManagedByMultiSelect';
import { getRoleLabel, getRoleLabelPlural } from '@/utils/role-label-utils';
import type { StudentSituation } from '@/types/remote/student.types';
import type { BadgeVariant } from '@/components/common/LabelBadge';
import {
  PERSON_PENDING_STATUS_COLOR,
  PERSON_SUSPENDED_STATUS_COLOR,
  STUDENT_ACTIVE_STATUS_CONFIG,
} from '@/constants/shared-catalog';

export type { StudentActiveStatus, StudentPendingStatus } from '@/constants/shared-catalog';

export {
  BOOLEAN_PARTICIPATION_OPTIONS,
  BOOLEAN_SELECT_OPTIONS,
  COURSE_PATH_TYPE_CONFIG,
  ESTABLISHMENT_TYPE_TO_CYCLE,
  GENDER_APPEARANCE,
  HIGH_SCHOOL_CURRICULUM_OPTIONS,
  REMOTE_SUBJECT_CONFIG,
  REMOTE_SUBJECT_OPTIONS,
  SCHOOL_SITUATION_CONFIG,
  STEP_COMPLETION_CONFIG,
  STUDENT_ACTIVE_STATUS_CONFIG,
  STUDENT_GENDER_LABELS,
  STUDENT_GENDER_OPTIONS,
  STUDENT_GENDER_OPTIONS_WITHOUT_OTHER,
  STUDENT_PENDING_STATUS_CONFIG,
  STUDENT_SITUATION_CONFIG,
  YES_NO_BADGE_CONFIG,
  type CoursePathType,
  type StudentCategory,
} from '@/constants/shared-catalog';

/** Badge + icon config for a student status. */
export interface StudentStatusConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor?: string;
}

/** In-person filter group ids (`interface_elements.element_id`). */
export type FilterGroupId =
  | 'filter-search'
  | 'filter-status'
  | 'filter-grade-level'
  | 'filter-subject'
  | 'filter-establishment'
  | 'filter-class'
  | 'filter-specialties'
  | 'filter-dates'
  | 'filter-is-minor'
  | 'filter-special-needs'
  | 'filter-accompaniment-type'
  | 'filter-availability'
  | 'filter-exit-date'
  | 'filter-exit-reason';

/** Badge config for each in-person student status. */
export const STUDENT_STATUS_CONFIG: Record<string, StudentStatusConfig> = {
  registered: {
    label: 'Inscrit',
    icon: Clock,
    badgeVariant: 'outlined',
    badgeColor: PERSON_PENDING_STATUS_COLOR,
  },
  accompanied: {
    label: 'Accompagné',
    icon: CheckCircle2,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-success))',
  },
  deactivated: {
    label: 'Désactivé',
    icon: MinusCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
  },
};

/** Status {@link Select} options in the in-person student edit form (restricted transitions). */
export interface StudentStatusSelectOption {
  value: StudentAccountStatus;
  label: string;
}

/** Extra context for the restricted Status {@link Select} on the in-person edit form. */
export interface StudentEditStatusSelectContext {
  /** Status immediately before the last transition to `deactivated` in `student_status_history`. */
  previousStatusBeforeDeactivate?: StudentAccountStatus | null;
  /** Fallback from the deactivated-student GET (`reactivationTargetStatus`, session attendance). */
  reactivationTargetStatus?: StudentAccountStatus | null;
}

/** In-person student exit-reason select options. */
export const EXIT_REASON_OPTIONS = [
  { value: 'early_exit', label: "Fin d'engagement anticipée" },
  { value: 'no_follow_up', label: 'Ne donne pas suite' },
  { value: 'other', label: 'Autre' },
] as const;

/** Canonical exit-reason value. */
export type ExitReasonOption = (typeof EXIT_REASON_OPTIONS)[number]['value'];

/** Exit-reason values as a flat list. */
export const EXIT_REASON_VALUES = EXIT_REASON_OPTIONS.map((o) => o.value);

/** Coerces an undefined exit reason to an empty string. */
export function normalizeExitReason(v: string | undefined): string {
  return v ?? '';
}

/** Default in-person student list filters. */
export const DEFAULT_STUDENT_FILTERS: StudentFilters = {
  search: undefined,
  statuses: [],
  levelIds: [],
  subjects: [],
  establishmentId: undefined,
  classIds: [],
  specialtyIds: [],
  createdFrom: undefined,
  createdTo: undefined,
  isMinor: undefined,
  hasSpecialNeeds: undefined,
  accompanimentTypes: [],
  exitDateFrom: undefined,
  exitDateTo: undefined,
  exitReason: undefined,
  availability: [],
};

/** All in-person filter group ids (configure-filters panel). */
export const ALL_FILTER_GROUP_IDS: FilterGroupId[] = [
  'filter-search',
  'filter-status',
  'filter-grade-level',
  'filter-subject',
  'filter-establishment',
  'filter-class',
  'filter-specialties',
  'filter-dates',
  'filter-is-minor',
  'filter-special-needs',
  'filter-accompaniment-type',
  'filter-availability',
  'filter-exit-date',
  'filter-exit-reason',
];

/** Badge + icon for an in-person accompaniment-type filter chip. */
export interface StudentAccompanimentTypeConfig {
  label: string;
  icon: LucideIcon;
  badgeColor: string;
}

/** Collective vs individual — same icons/colors as session planification cards. */
export const STUDENT_ACCOMPANIMENT_TYPE_CONFIG: Record<
  StudentAccompanimentType,
  StudentAccompanimentTypeConfig
> = {
  individual: {
    label: 'Accompagnement individuel',
    icon: User,
    badgeColor: 'var(--color-1-nuance-1)',
  },
  collective: {
    label: 'Accompagnement collectif',
    icon: Users,
    badgeColor: 'var(--color-2-nuance-1)',
  },
};

/** Human-readable label per in-person filter group (configure panel). */
export const FILTER_GROUP_LABELS: Record<FilterGroupId, string> = {
  'filter-search': 'Recherche',
  'filter-status': 'Statut',
  'filter-grade-level': 'Niveau',
  'filter-subject': 'Matière',
  'filter-establishment': 'Établissement',
  'filter-class': 'Classe',
  'filter-specialties': 'Spécialités',
  'filter-dates': "Date d'entrée dans le dispositif",
  'filter-is-minor': 'Mineur',
  'filter-special-needs': 'Besoins spécifiques',
  'filter-accompaniment-type': "Type d'accompagnement",
  'filter-availability': 'Disponibilité',
  'filter-exit-date': 'Date de désactivation',
  'filter-exit-reason': 'Motif de sortie',
};

/** Disability-type select options. */
export const DISABILITY_TYPE_OPTIONS = [
  { value: 'motor', label: 'Handicap moteur' },
  { value: 'visual', label: 'Handicap visuel' },
  { value: 'hearing', label: 'Handicap auditif' },
  { value: 'intellectual', label: 'Handicap intellectuel' },
  { value: 'psychiatric', label: 'Handicap psychique' },
  { value: 'cognitive', label: 'Handicap cognitif' },
  { value: 'asd', label: "Troubles du spectre de l'autisme (TSA)" },
  { value: 'ndd', label: 'Troubles du neuro développement (TND)' },
  { value: 'dys', label: 'Troubles du langage et des apprentissages (dys)' },
  { value: 'chronic_illness', label: 'Maladies invalidantes ou chroniques' },
  { value: 'multiple_disabilities', label: 'Polyhandicap' },
  { value: 'combined_disabilities', label: 'Pluri handicap' },
  { value: 'other', label: 'Autre' },
] satisfies { value: string; label: string }[];

/** Display labels for disability types. */
export const DISABILITY_TYPE_LABEL: Record<string, string> = Object.fromEntries(
  DISABILITY_TYPE_OPTIONS.map((o) => [o.value, o.label]),
);

/** Coerces an undefined disability type to an empty string. */
export function normalizeDisabilityType(v: string | undefined): string {
  return v ?? '';
}

/** Special-needs type select options. */
export const SPECIAL_NEEDS_TYPE_OPTIONS = [
  { value: 'ulis', label: 'ULIS' },
  { value: 'upe2a', label: 'UPE2A' },
  { value: 'segpa', label: 'SEGPA' },
  { value: 'allophones', label: 'Allophones' },
  { value: 'erea', label: 'EREA' },
  { value: 'autre', label: 'Autre' },
] satisfies { value: string; label: string }[];

/** Known special-needs type values (for custom-vs-catalog checks). */
export const KNOWN_SPECIAL_NEEDS_TYPES = new Set(SPECIAL_NEEDS_TYPE_OPTIONS.map((o) => o.value));

/** Remote student management tab ids. */
export type StudentRemoteTabView = 'students' | 'pre_registered' | 'suspended' | 'archived';

/** Remote student tab labels. */
export const STUDENT_REMOTE_TAB_LABELS: Record<StudentRemoteTabView, string> = {
  get students() {
    return getRoleLabelPlural('student');
  },
  pre_registered: 'Pré-inscrits',
  suspended: 'Suspendu',
  archived: 'Archivés',
};

/** Sequential tab colors — shared between dashboard section and management page. */
export const STUDENT_REMOTE_TAB_COLORS: Record<StudentRemoteTabView, string> = {
  students: 'var(--color-1-nuance-1)',
  pre_registered: PERSON_PENDING_STATUS_COLOR,
  suspended: PERSON_SUSPENDED_STATUS_COLOR,
  archived: 'rgb(var(--color-error))',
};

/** Remote student tab icons. */
export const STUDENT_REMOTE_TAB_ICONS: Record<StudentRemoteTabView, LucideIcon> = {
  students: Users,
  pre_registered: Clock,
  suspended: PauseCircle,
  archived: Archive,
};

/** One forced list `userStatus` per remote student tab — do not send `tabView`. */
export const STUDENT_REMOTE_TAB_USER_STATUS: Record<StudentRemoteTabView, string> = {
  students: 'active',
  pre_registered: 'registered',
  suspended: 'suspended',
  archived: 'archived',
};

/** Badge + icon config for a remote student status. */
export interface StudentRemoteStatusConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor?: string;
}

// Account-level status only (no tutor pairing) when status and situation are separate chips.
export const STUDENT_ACCOUNT_STATUS_CONFIG: Record<string, StudentRemoteStatusConfig> = {
  pending: {
    label: 'En attente',
    icon: Clock,
    badgeVariant: 'outlined',
    badgeColor: PERSON_PENDING_STATUS_COLOR,
  },
  registered: {
    label: 'Inscrit',
    icon: Clock,
    badgeVariant: 'outlined',
    badgeColor: PERSON_PENDING_STATUS_COLOR,
  },
  active: {
    label: 'Actif',
    icon: CheckCircle2,
    badgeVariant: 'active',
    badgeColor: 'rgb(var(--status-success))',
  },
  suspended: {
    label: 'Suspendu',
    icon: PauseCircle,
    badgeVariant: 'outlined',
    badgeColor: PERSON_SUSPENDED_STATUS_COLOR,
  },
  archived: {
    label: 'Archivé',
    icon: Archive,
    badgeVariant: 'ghost',
    badgeColor: 'rgb(var(--color-error))',
  },
  deleted: { label: 'Supprimé', icon: XCircle, badgeVariant: 'disabled' },
};

/** Registration milestone chip config. */
export interface StudentMilestoneConfig {
  key: string;
  label: string;
  icon: LucideIcon;
  validatedColor: string;
  pendingColor: string;
}

/** Remote student registration milestones. */
export const STUDENT_REMOTE_MILESTONES: StudentMilestoneConfig[] = [
  {
    key: 'registration_information',
    label: 'Infos',
    icon: UserCircle,
    validatedColor: 'rgb(var(--status-success))',
    pendingColor: 'rgb(var(--status-neutral))',
  },
  {
    key: 'matching_data',
    label: 'Dispo',
    icon: CalendarDays,
    validatedColor: 'rgb(var(--status-success))',
    pendingColor: 'rgb(var(--status-neutral))',
  },
  {
    key: 'welcome_meeting',
    label: 'RA',
    icon: Users,
    validatedColor: 'rgb(var(--status-success))',
    pendingColor: 'rgb(var(--status-neutral))',
  },
  {
    key: 'documents',
    label: 'Docs',
    icon: FileText,
    validatedColor: 'rgb(var(--status-success))',
    pendingColor: 'rgb(var(--status-neutral))',
  },
];

/** Remote student detail section labels. */
export const STUDENT_REMOTE_SECTION_LABELS: Record<string, string> = {
  infos_famille: 'Informations famille',
  infos_eleve: 'Informations élève',
  disponibilites: 'Disponibilités',
  welcome: "Réunion d'accueil",
  scoring: 'Scoring',
  seances: 'Séances',
  report: 'Comptes rendus',
  historique: 'Historique',
};

/** Remote student registration step labels. */
export const STUDENT_REMOTE_STEP_LABELS: Record<string, string> = {
  registration_information: "Informations d'inscription",
  matching_data: 'Disponibilités',
  welcome_meeting: "Réunion d'accueil",
  documents: 'Documents',
};

/** Remote student filter group ids. */
export type StudentRemoteFilterGroupId =
  | 'filter-onboarding-status'
  | 'filter-folder-situation'
  | 'filter-registration-step'
  | 'filter-linked-parent'
  | 'filter-search'
  | 'filter-program'
  | 'filter-subject'
  | 'filter-level'
  | 'filter-partner'
  | 'filter-department'
  | 'filter-establishment'
  | 'filter-studies-participation'
  | 'filter-gender'
  | 'filter-managed-by';

/** Human-readable label per remote student filter group. */
export const STUDENT_REMOTE_FILTER_LABELS: Record<StudentRemoteFilterGroupId, string> = {
  'filter-onboarding-status': 'Statut',
  'filter-folder-situation': 'Situation',
  'filter-registration-step': "Étape d'inscription",
  get 'filter-linked-parent'() {
    return getRoleLabel('parent');
  },
  'filter-search': 'Recherche',
  'filter-program': 'Programme',
  'filter-subject': 'Matière',
  'filter-level': "Niveau de l'élève",
  'filter-partner': 'Partenaire',
  'filter-department': 'Département',
  'filter-establishment': 'Établissement',
  'filter-studies-participation': 'Participation aux études',
  'filter-gender': 'Genre',
  'filter-managed-by': 'Géré par',
};

/** Filter state for the remote students {@link DataTable}. Single source of truth. */
export interface StudentRemoteFilters {
  onboardingStatus?: string;
  folderSituation?: string;
  studentSituation?: string;
  managedByIds?: ManagedByValue;
  parentId?: string;
  search?: string;
  programId?: string;
  subject?: string;
  levelId?: string;
  partnerId?: string;
  establishmentId?: string;
  stepKeys?: string[];
  participationStudies?: boolean;
  gender?: string;
  departmentCodes?: string[];
  userStatus?: string;
}

/** Default remote student list filters. */
export const DEFAULT_STUDENT_REMOTE_FILTERS: StudentRemoteFilters = {};

/** All remote student filter group ids (configure-filters panel). */
export const ALL_STUDENT_REMOTE_FILTER_GROUP_IDS: StudentRemoteFilterGroupId[] = [
  'filter-onboarding-status',
  'filter-folder-situation',
  'filter-registration-step',
  'filter-linked-parent',
  'filter-search',
  'filter-program',
  'filter-subject',
  'filter-level',
  'filter-partner',
  'filter-department',
  'filter-establishment',
  'filter-managed-by',
  'filter-gender',
  'filter-studies-participation',
];

/** Matching-view subset — hides Status, Situation and Establishment. */
export const ALL_STUDENT_MATCHING_FILTER_GROUP_IDS: StudentRemoteFilterGroupId[] = [
  'filter-registration-step',
  'filter-linked-parent',
  'filter-search',
  'filter-program',
  'filter-subject',
  'filter-level',
  'filter-partner',
  'filter-gender',
  'filter-managed-by',
];

/** Remote student registration-step filter options. */
export const STUDENT_REGISTRATION_STEP_OPTIONS = [
  {
    value: 'registration_information',
    label: "Informations de l'enfant",
    icon: User,
    color: 'rgb(var(--color-info))',
  },
  {
    value: 'matching_data',
    label: 'Tutorat',
    icon: GraduationCap,
    color: 'var(--color-1-nuance-1)',
  },
  {
    value: 'welcome_meeting',
    label: "Réunion d'accueil",
    icon: Users,
    color: 'rgb(var(--status-warning))',
  },
  { value: 'documents', label: 'Documents', icon: FileText, color: 'var(--color-2-nuance-1)' },
] as const;

/** School-situation select options. */
export const SCHOOL_SITUATION_OPTIONS = [
  { value: 'primary', label: 'Élève du primaire' },
  { value: 'middle_school', label: 'Collégien' },
  { value: 'high_school', label: 'Lycéen' },
] as const;

/** Legacy (French / mixed-case) → canonical English school situation values. */
/** Legacy French / mixed-case school-situation labels → canonical values. */
export const SCHOOL_SITUATION_LEGACY_MAP: Record<string, string> = {
  Primaire: 'primary',
  primaire: 'primary',
  'Élève du primaire': 'primary',
  College: 'middle_school',
  college: 'middle_school',
  Collège: 'middle_school',
  Collégien: 'middle_school',
  Collegien: 'middle_school',
  Lycée: 'high_school',
  lycee: 'high_school',
  Lycee: 'high_school',
  Lycéen: 'high_school',
  Lyceen: 'high_school',
};

/** School-curriculum select options. */
export const SCHOOL_CURRICULUM_OPTIONS = [
  { value: 'classic', label: 'Classique' },
  { value: 'bilingual', label: 'Bilingue' },
  { value: 'international', label: 'International' },
  { value: 'other', label: 'Autre' },
] as const;

/** Maps canonical schoolCurriculum values to French display labels. */
export const SCHOOL_CURRICULUM_LABEL: Record<string, string> = Object.fromEntries(
  SCHOOL_CURRICULUM_OPTIONS.map((o) => [o.value, o.label]),
);

/** Legacy (French / mixed-case) → canonical English curriculum values. */
/** Legacy French / mixed-case curriculum labels → canonical values. */
export const SCHOOL_CURRICULUM_LEGACY_MAP: Record<string, string> = {
  classique: 'classic',
  Classique: 'classic',
  bilingue: 'bilingual',
  Bilingue: 'bilingual',
  International: 'international',
  autre: 'other',
  Autre: 'other',
};

/** Welcome-meeting participation badge config. */
export const WELCOME_MEETING_PARTICIPATION_CONFIG = {
  done: {
    label: "Réunion d'accueil effectuée",
    icon: CheckCircle2,
    badgeVariant: 'active' as const,
  },
  pending: {
    label: "Réunion d'accueil non effectuée",
    icon: Clock,
    badgeVariant: 'outlined' as const,
    badgeColor: 'rgb(var(--status-warning))',
  },
};

/**
 * Status-filter keys for the students tab.
 * Pre-registered and suspended tabs lock their own status display.
 */
export type StudentRemoteStatusFilterKey = 'pending' | 'active' | 'suspended' | 'archived';

/** Status-filter keys shown on the Students tab. */
export const STUDENT_ACTIVE_TAB_STATUS_FILTER_KEYS = [
  'active',
] as const satisfies readonly StudentRemoteStatusFilterKey[];

/** Status-filter chip config for remote student tabs. */
export const STUDENT_STATUS_FILTER_CHIP_CONFIG: Record<
  StudentRemoteStatusFilterKey,
  StudentRemoteStatusConfig
> = {
  pending: STUDENT_ACCOUNT_STATUS_CONFIG.pending,
  active: STUDENT_ACTIVE_STATUS_CONFIG.active_without_tutor,
  suspended: STUDENT_ACCOUNT_STATUS_CONFIG.suspended,
  archived: STUDENT_ACCOUNT_STATUS_CONFIG.archived,
};

/** Situations shown in the status filter for the active tab. */
export const STUDENT_ACTIVE_TAB_SITUATION_FILTER_KEYS = [
  'awaiting_tutor',
  'tutoring_in_progress',
] as const satisfies readonly StudentSituation[];

/** Remote student program select options. */
export const STUDENT_PROGRAM_OPTIONS = [
  { value: 'homeclass', label: 'Homeclass' },
  { value: 'zupdefoot', label: 'ZupdeFoot' },
  { value: 'tutorat_solidaire', label: 'Tutorat Solidaire' },
] as const;

/** Canonical student program value. */
export type StudentProgramValue = (typeof STUDENT_PROGRAM_OPTIONS)[number]['value'];

/** Program badge config for remote students. */
export const STUDENT_PROGRAM_CONFIG: Record<string, StudentRemoteStatusConfig> = {
  homeclass: {
    label: 'Homeclass',
    icon: Home,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-2-nuance-1)',
  },
  zupdefoot: {
    label: 'ZupdeFoot',
    icon: Trophy,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-warning))',
  },
  tutorat_solidaire: {
    label: 'Tutorat Solidaire',
    icon: Heart,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-success))',
  },
};

/** Labels for student availability boolean flags. */
export const STUDENT_AVAILABILITY_BOOL_LABELS: Record<string, string> = {
  availableOnVacations: 'Vacances scolaires',
  availableOnWeekends: 'Week-ends',
  availableOnHolidays: 'Jours fériés',
};

/** Remote student detail panel tabs. */
export type StudentDetailTab =
  | 'family'
  | 'profile'
  | 'tutors'
  | 'availability'
  | 'welcome_meeting'
  | 'history';

/** Tabs on the remote student detail view. */
export const STUDENT_DETAIL_TAB_CONFIGS: Array<{
  id: StudentDetailTab;
  label: string;
  icon: LucideIcon;
}> = [
  { id: 'family', label: 'Infos famille', icon: Users },
  { id: 'profile', label: 'Infos élève', icon: User },
  { id: 'tutors', label: 'Mes tuteurs', icon: UserCheck },
  { id: 'availability', label: 'Disponibilités', icon: CalendarDays },
  { id: 'welcome_meeting', label: "Réunion d'accueil", icon: BookOpen },
  { id: 'history', label: 'Historique', icon: History },
];

/** React Query keys for student form lookups. */
export const STUDENT_FORM_QUERY_KEYS = {
  roles: ['roles'] as const,
  subjects: ['subjects'] as const,
  levels: ['levels', 'all'] as const,
  specialtiesActive: ['specialties', 'active'] as const,
  displayNameCheck: (query: string) => ['students-displayname-check', query] as const,
  displayNameCheckEdit: (query: string) => ['students-displayname-check-edit', query] as const,
  student: (id: string) => ['students', id] as const,
  studentStatusHistory: (id: string) => ['students', id, 'status-history'] as const,
  schoolClasses: (establishmentId: string, levelId: string) =>
    ['school-classes', establishmentId, levelId] as const,
  establishmentType: (establishmentId: string) =>
    ['establishments', establishmentId, 'type'] as const,
  usersStudent: ['users', 'student'] as const,
};

/** Filename for the student CSV import template. */
export const STUDENT_IMPORT_TEMPLATE_FILENAME = 'modele_import_eleves.xlsx';
